/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
// eslint-disable-next-line no-restricted-imports
import Image, { type ImageProps, type StaticImageData } from 'next/image';

// TODO: Revert back to `type NextImageProps = Omit<ImageProps, 'alt'> & { alt?: string })` when we upgrade `@types/react` to v19 (https://jira.autozone.com/browse/SB-4799)

type NextImageProps = {
  src: string | StaticImageData;
  alt?: string;
  width?: number | `${number}`;
  height?: number | `${number}`;
  className?: string;
  style?: React.CSSProperties;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  unoptimized?: boolean;
  fill?: boolean;
  sizes?: string;
  loader?: ImageProps['loader'];
  onLoad?: () => void;
  onError?: (e?: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  id?: string;
  tabIndex?: number;
  role?: string;
  title?: string;
  draggable?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseOut?: () => void;
  onMouseOver?: () => void;
  onAuxClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
  layout?: 'fixed' | 'fill' | 'intrinsic' | 'responsive';
  loading?: 'lazy' | 'eager';
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  'data-testid'?: string;
  'data-index'?: number;
  'data-searchresultposition'?: number;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  itemProp?: string;
};

// TODO: Can we use optimized Next image?
const NextImage = (props: NextImageProps) => {
  const {
    src,
    alt = '',
    unoptimized = true,
    width,
    height,
    className,
    style,
    priority,
    quality,
    placeholder,
    blurDataURL,
    fill,
    sizes,
    loader,
    onLoad,
    onError,
    id,
    tabIndex,
    role,
    title,
    draggable,
    onClick,
    onMouseEnter,
    onMouseOut,
    onMouseOver,
    onAuxClick,
    layout,
    loading,
    objectFit,
    'data-testid': dataTestId,
    'data-index': dataIndex,
    'data-searchresultposition': dataSearchResultPosition,
    'aria-label': ariaLabel,
    'aria-expanded': ariaExpanded,
    itemProp,
  } = props;

  if (!src) return null;

  return (
    <Image
      src={src}
      alt={alt}
      unoptimized={unoptimized}
      width={width}
      height={height}
      className={className}
      style={style}
      priority={priority}
      quality={quality}
      placeholder={placeholder}
      blurDataURL={blurDataURL}
      fill={fill}
      sizes={sizes}
      loader={loader}
      onLoad={onLoad}
      onError={onError}
      id={id}
      tabIndex={tabIndex}
      role={role}
      title={title}
      draggable={draggable}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      onAuxClick={onAuxClick}
      layout={layout}
      loading={loading}
      objectFit={objectFit}
      data-testid={dataTestId}
      data-index={dataIndex}
      data-searchresultposition={dataSearchResultPosition}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      itemProp={itemProp}
    />
  );
};

export default NextImage;

type AkamaiLoaderOptions = {
  src: string;
  width: number;
};

type PureLoaderOptions = { src: string };

const akamaiLoader = ({ src, width }: AkamaiLoaderOptions): string => {
  return `${src.trim()}?imwidth=${width}`;
};

const pureLoader = ({ src }: PureLoaderOptions): string => src;

export const blurDataURLPlaceholder: string =
  'data:image/octet-stream;base64,UklGRiACAABXRUJQVlA4WAoAAAAAAAAAYwAAYwAAVlA4IKwBAADwCQCdASpkAGQAAAAAJaQAOeJZHKp4AaWWaL5FqwbRj5k8ZaxypN+d9cywoGpSySja8UwIAE3tZt8sttHDFXo6ViFLPk6aLyRSFF8NUY2E0bjTaJjTaJUAAP7/3tzwABz9H9Bf281Q5//V94oKmf7jeiLfzCjSHpwbOvhp4tNrLWBFu/ng4YHb9MJHChPbyD9HRHdviZiIeQgX0UbhienlqEWeF2sBeg/feAt0VaxuaMP1w61BZd86C/qJA35KXETN5HsxuIL8kc7wsSR4o9CzPy93FURvbOcXxZpBawvHI7w5lEp09fUv7wI4JkITCOuVxcMeZgcANFZ2xA7jKsAyrnAxC5cImChL3AYVeHhd15jIuN5ibAJcDRUCOF0Fhy6Fx/EXsiGA8RHSVXPQPtYNvHqrBIbb1sAHZ4eJrsY6kIbEocGYJOE4izqUOnmiDk6t7QXkZBC57TGjWXTaTVLeuOrdqrqfuU73m4E7GmOik+8i7FgxqmlPba4MbrQ19BH9rmByYsFqyTXPpnLrorY+iJOL2nwPglSZ9B7SznxMfXhPQVeiUAAAAAAAAFBTQUlOAAAAOEJJTQPtAAAAAAAQASwAAAABAAEBLAAAAAEAAThCSU0EKAAAAAAADAAAAAI/8AAAAAAAADhCSU0EQwAAAAAADlBiZVcBEAAGAGQAAAAA';

export { akamaiLoader, pureLoader };
